import { Fragment, useState, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  reportId?: string;
  teamId?: string;
  read: boolean;
  createdAt: string;
}

export default function NotificationBell() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  // Fetch unread count
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await notificationApi.getUnreadCount();
      return response.data;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Fetch notifications when dropdown opens
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await notificationApi.getNotifications({ limit: 10 });
      return response.data;
    },
    enabled: isOpen,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationIds: string[]) => notificationApi.markAsRead(notificationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (notificationIds: string[]) => notificationApi.deleteNotifications(notificationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const unreadCount = unreadData?.count || 0;
  const notifications = notificationsData?.notifications || [];

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate([notification.id]);
    }
    setIsOpen(false);
  };

  return (
    <Menu as="div" className="relative ml-3">
      {({ open }) => (
        <MenuContent
          open={open}
          setIsOpen={setIsOpen}
          unreadCount={unreadCount}
          isLoading={isLoading}
          notifications={notifications}
          handleNotificationClick={handleNotificationClick}
          markAllAsReadMutation={markAllAsReadMutation}
          deleteMutation={deleteMutation}
        />
      )}
    </Menu>
  );
}

function MenuContent({
  open,
  setIsOpen,
  unreadCount,
  isLoading,
  notifications,
  handleNotificationClick,
  markAllAsReadMutation,
  deleteMutation,
}: {
  open: boolean;
  setIsOpen: (v: boolean) => void;
  unreadCount: number;
  isLoading: boolean;
  notifications: Notification[];
  handleNotificationClick: (n: Notification) => void;
  markAllAsReadMutation: any;
  deleteMutation: any;
}) {
  useEffect(() => {
    setIsOpen(open);
  }, [open, setIsOpen]);

  return (
    <>
      <Menu.Button className="relative p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2">
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-96 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllAsReadMutation.mutate()}
                      className="text-xs text-ocean-600 hover:text-ocean-700 font-medium"
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                {/* Notifications list */}
                <div className="max-h-96 overflow-y-auto">
                  {isLoading ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      Loading...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.map((notification: Notification) => (
                      <Menu.Item key={notification.id}>
                        {({ active }) => (
                          <div
                            className={clsx(
                              'relative border-b border-gray-100 last:border-b-0',
                              !notification.read && 'bg-blue-50'
                            )}
                          >
                            {notification.reportId ? (
                              <Link
                                to={`/reports/${notification.reportId}`}
                                onClick={() => handleNotificationClick(notification)}
                                className={clsx(
                                  'block px-4 py-3 hover:bg-gray-50 transition-colors',
                                  active && 'bg-gray-50'
                                )}
                              >
                                <NotificationContent notification={notification} />
                              </Link>
                            ) : (
                              <div
                                onClick={() => handleNotificationClick(notification)}
                                className={clsx(
                                  'block px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors',
                                  active && 'bg-gray-50'
                                )}
                              >
                                <NotificationContent notification={notification} />
                              </div>
                            )}

                            {/* Delete button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                deleteMutation.mutate([notification.id]);
                              }}
                              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Delete notification"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </Menu.Item>
                    ))
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="px-4 py-2 border-t border-gray-200 text-center">
                    <Link
                      to="/notifications"
                      className="text-xs text-ocean-600 hover:text-ocean-700 font-medium"
                      onClick={() => setIsOpen(false)}
                    >
                      View all notifications
                    </Link>
                  </div>
                )}
              </Menu.Items>
            </Transition>
    </>
  );
}

function NotificationContent({ notification }: { notification: Notification }) {
  const icon = getNotificationIcon(notification.type);
  const colorClass = getNotificationColor(notification.type);

  return (
    <div className="flex items-start">
      <div className={clsx('text-2xl mr-3 flex-shrink-0', colorClass)}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={clsx('text-sm font-medium', notification.read ? 'text-gray-700' : 'text-gray-900')}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </p>
      </div>
      {!notification.read && (
        <div className="ml-2 flex-shrink-0">
          <span className="inline-block h-2 w-2 rounded-full bg-blue-600"></span>
        </div>
      )}
    </div>
  );
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'APPROVAL_SUBMITTED':
      return '📋';
    case 'APPROVAL_APPROVED':
      return '✅';
    case 'APPROVAL_REJECTED':
      return '⚠️';
    case 'REPORT_READY':
      return '📊';
    case 'TEAM_INVITE':
      return '👥';
    default:
      return '🔔';
  }
}

function getNotificationColor(type: string) {
  switch (type) {
    case 'APPROVAL_SUBMITTED':
      return 'text-blue-600';
    case 'APPROVAL_APPROVED':
      return 'text-green-600';
    case 'APPROVAL_REJECTED':
      return 'text-amber-600';
    case 'REPORT_READY':
      return 'text-teal-600';
    case 'TEAM_INVITE':
      return 'text-indigo-600';
    default:
      return 'text-gray-600';
  }
}
